import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Coins, Wallet, TrendingUp, RotateCcw, AlertCircle, Clock, Calculator, Loader2, Check } from 'lucide-react';
import PropertyFeaturesBlock from './PropertyFeaturesBlock';
import HelpHint from './HelpHint';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Pestaña "Alquiler" — estructura visual + campos preparados.
// FASE 1: solo maqueta y estado. Los cálculos, MIVAU, Idealista, media 3m,
// multiplicador, histórico y ROI real se implementarán en fases posteriores.
// Todos los valores que aún no están conectados muestran "Pendiente de configurar"
// o "Pendiente de datos" — NUNCA ceros engañosos.

const fmtMoney = (v, opts = {}) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return null;
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR',
    maximumFractionDigits: opts.decimals ?? 2, minimumFractionDigits: opts.decimals ?? 2,
  }).format(Number(v));
};

const fmtM2Price = (v) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return null;
  return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(v))} €/m²`;
};

const fmtPercent = (v, decimals = 0) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return null;
  return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: decimals, minimumFractionDigits: decimals }).format(Number(v))} %`;
};

const fmtDateTime = (iso) => {
  if (!iso) return 'Sin calcular';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'Sin calcular';
    return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      .replace(',', ' ·');
  } catch { return 'Sin calcular'; }
};

// Placeholder visual cuando un valor no está aún conectado/calculado.
const Pending = ({ text = 'Pendiente de configurar' }) => (
  <span className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>{text}</span>
);

// Fila de dato → label izquierda / valor derecha
const InfoRow = ({ label, value, help, tooltip, highlight = false, testid }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0" style={{ borderColor: 'var(--color-border)' }}>
    <div className="flex-1 min-w-0">
      <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
        {label}
        {tooltip && <HelpHint text={tooltip} />}
      </p>
      {help && <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{help}</p>}
    </div>
    <div className={`text-right font-medium tabular-nums ${highlight ? 'text-lg' : 'text-sm'}`}
         style={{ color: highlight ? 'var(--color-text-primary)' : 'var(--color-text-primary)' }}
         data-testid={testid}>
      {value ?? <Pending />}
    </div>
  </div>
);

const Card = ({ title, icon: Icon, children, className = '', testid }) => (
  <div className={`card-base p-5 ${className}`} data-testid={testid}>
    <h3 className="heading-3 flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-blue-600" />}
      {title}
    </h3>
    <div className="space-y-0">{children}</div>
  </div>
);

// Estado del análisis. FASE 1: siempre "Pendiente de configurar" salvo que
// falten datos mínimos del activo (municipio / superficie / precio de compra).
const computeStatus = ({ municipio, superficie, precioCompra }) => {
  if (!municipio) return { label: 'Falta municipio', color: 'amber' };
  if (!superficie || superficie <= 0) return { label: 'Falta superficie', color: 'amber' };
  if (precioCompra == null) return { label: 'Falta precio de compra', color: 'amber' };
  return { label: 'Pendiente de configurar', color: 'slate' };
};

const RentalAnalysisSection = ({ asset, onAssetUpdate }) => {
  const rental = asset?.rental_analysis || {};
  const automaticArea = asset?.metros_totales ?? asset?.metros ?? asset?.superficie ?? null;

  // Superficie utilizada para el alquiler. Se persiste como campo top-level
  // `superficie_alquiler` en el activo. Si no hay override, se muestra e inicializa
  // con la superficie general del activo; al guardar se convierte en el valor
  // usado siempre para los cálculos de alquiler de este activo.
  const storedRentalArea = asset?.superficie_alquiler;
  const initialAreaValue = storedRentalArea != null ? storedRentalArea : (automaticArea != null ? automaticArea : '');
  const [areaUsed, setAreaUsed] = useState(String(initialAreaValue));
  const [savingArea, setSavingArea] = useState(false);
  // Sincroniza el input cuando cambia el activo (recarga externa).
  useEffect(() => {
    const v = storedRentalArea != null ? storedRentalArea : (automaticArea != null ? automaticArea : '');
    setAreaUsed(String(v));
  }, [asset?.id, storedRentalArea, automaticArea]);

  // Renta mensual manual — opcional. Persistencia definitiva en próxima fase.
  const [manualRent, setManualRent] = useState(rental.manual_monthly_rent ?? '');
  const [savingManualRent, setSavingManualRent] = useState(false);
  const [manualRentSavedAt, setManualRentSavedAt] = useState(null); // 'manual' | 'auto' | null
  useEffect(() => {
    setManualRent(rental.manual_monthly_rent ?? '');
  }, [rental.manual_monthly_rent]);
  useEffect(() => {
    if (!manualRentSavedAt) return;
    const t = setTimeout(() => setManualRentSavedAt(null), 3200);
    return () => clearTimeout(t);
  }, [manualRentSavedAt]);

  const saveManualRent = async (mode /* 'save' | 'auto' */) => {
    let payloadValue;
    if (mode === 'auto') {
      payloadValue = null;
    } else {
      const trimmed = String(manualRent ?? '').trim();
      if (trimmed === '') {
        toast.error('Introduce una renta mensual manual antes de guardar');
        return;
      }
      const num = Number(trimmed);
      if (Number.isNaN(num) || num <= 0) {
        toast.error('La renta manual debe ser un número positivo');
        return;
      }
      payloadValue = num;
    }
    setSavingManualRent(true);
    try {
      const res = await axios.patch(
        `${API}/assets/${asset.id}/rental-analysis`,
        { manual_monthly_rent: payloadValue },
        { withCredentials: true },
      );
      const newRental = res.data?.rental_analysis || {};
      if (typeof onAssetUpdate === 'function') {
        onAssetUpdate({ ...asset, rental_analysis: newRental });
      }
      setManualRentSavedAt(mode === 'auto' ? 'auto' : 'manual');
      toast.success(mode === 'auto' ? 'Estimación automática restaurada' : 'Guardado y ROI actualizado');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo guardar la renta manual';
      toast.error(typeof detail === 'string' ? detail : 'No se pudo guardar la renta manual');
    } finally {
      setSavingManualRent(false);
    }
  };

  // Default global de "Gastos de adquisición estimados" desde Parámetros.
  // Se carga una vez al montar la sección; si falla se cae a 15%.
  const [globalDefaultPct, setGlobalDefaultPct] = useState(15);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/rental`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const v = res.data?.acquisition_expenses_percentage;
        if (v != null && !Number.isNaN(Number(v))) setGlobalDefaultPct(Number(v));
      })
      .catch(() => {/* silencioso: usamos el fallback 15 */});
    return () => { cancelled = true; };
  }, []);

  // Override por-activo del % de gastos de adquisición.
  // '' = sin override → usará el default global.
  const [expensesPctInput, setExpensesPctInput] = useState(
    rental.acquisition_expenses_percentage != null ? String(rental.acquisition_expenses_percentage) : '',
  );
  const [savingExpensesPct, setSavingExpensesPct] = useState(false);
  useEffect(() => {
    setExpensesPctInput(
      rental.acquisition_expenses_percentage != null ? String(rental.acquisition_expenses_percentage) : '',
    );
  }, [rental.acquisition_expenses_percentage]);

  const effectiveExpensesPct = rental.acquisition_expenses_percentage != null
    ? Number(rental.acquisition_expenses_percentage)
    : globalDefaultPct;
  const isExpensesPctOverridden = rental.acquisition_expenses_percentage != null;

  const saveExpensesPct = async (rawValue) => {
    const trimmed = String(rawValue ?? '').trim();
    // Sin cambios → no hacer nada.
    const currentStored = rental.acquisition_expenses_percentage;
    const currentStr = currentStored != null ? String(currentStored) : '';
    if (trimmed === currentStr) return;

    let payloadValue;
    if (trimmed === '') {
      payloadValue = null;
    } else {
      const num = Number(trimmed);
      if (Number.isNaN(num) || num < 0 || num > 100) {
        toast.error('El porcentaje debe estar entre 0 y 100');
        setExpensesPctInput(currentStr);
        return;
      }
      payloadValue = num;
    }
    setSavingExpensesPct(true);
    try {
      const res = await axios.patch(
        `${API}/assets/${asset.id}/rental-analysis`,
        { acquisition_expenses_percentage: payloadValue },
        { withCredentials: true },
      );
      const newRental = res.data?.rental_analysis || {};
      toast.success('Gastos de adquisición actualizados');
      if (typeof onAssetUpdate === 'function') {
        onAssetUpdate({ ...asset, rental_analysis: newRental });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo actualizar el porcentaje';
      toast.error(typeof detail === 'string' ? detail : 'No se pudo actualizar el porcentaje');
      setExpensesPctInput(currentStr);
    } finally {
      setSavingExpensesPct(false);
    }
  };

  // Persistir superficie_alquiler como campo top-level del activo.
  // Vacío o igual a la superficie general → se envía null (borra el override).
  const saveRentalArea = async (rawValue) => {
    const trimmed = String(rawValue ?? '').trim();
    const currentStored = storedRentalArea;
    const currentStr = currentStored != null ? String(currentStored) : '';
    if (trimmed === currentStr) return;

    let payloadValue;
    if (trimmed === '') {
      payloadValue = null;
    } else {
      const num = Number(trimmed);
      if (Number.isNaN(num) || num <= 0) {
        toast.error('La superficie debe ser mayor que 0');
        setAreaUsed(currentStr || (automaticArea != null ? String(automaticArea) : ''));
        return;
      }
      payloadValue = num;
    }
    setSavingArea(true);
    try {
      const res = await axios.patch(
        `${API}/assets/${asset.id}/rental-analysis`,
        { superficie_alquiler: payloadValue },
        { withCredentials: true },
      );
      toast.success('Superficie de alquiler guardada');
      if (typeof onAssetUpdate === 'function') {
        onAssetUpdate({
          ...asset,
          superficie_alquiler: res.data?.superficie_alquiler ?? null,
          rental_analysis: res.data?.rental_analysis || asset.rental_analysis,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No se pudo guardar la superficie';
      toast.error(typeof detail === 'string' ? detail : 'No se pudo guardar la superficie');
      setAreaUsed(currentStr || (automaticArea != null ? String(automaticArea) : ''));
    } finally {
      setSavingArea(false);
    }
  };

  const status = useMemo(() => computeStatus({
    municipio: asset?.municipio,
    superficie: automaticArea,
    precioCompra: asset?.precio_compra,
  }), [asset?.municipio, automaticArea, asset?.precio_compra]);

  const statusStyles = {
    slate: { bg: 'rgba(148, 163, 184, 0.15)', text: '#64748b', border: 'rgba(148, 163, 184, 0.3)' },
    amber: { bg: 'rgba(251, 191, 36, 0.12)', text: '#b45309', border: 'rgba(251, 191, 36, 0.35)' },
    green: { bg: 'rgba(34, 197, 94, 0.12)', text: '#166534', border: 'rgba(34, 197, 94, 0.35)' },
  }[status.color] || {};

  return (
    <div className="space-y-6" data-testid="rental-analysis-section">
      {/* Cabecera: estado + fecha de actualización */}
      <div className="flex flex-wrap items-center justify-between gap-3 card-base p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full"
                style={{ background: statusStyles.bg, color: statusStyles.text, border: `1px solid ${statusStyles.border}` }}
                data-testid="rental-status-badge">
            <AlertCircle className="w-3 h-3" />
            {status.label}
          </span>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            La pestaña Alquiler estima el rendimiento en régimen de arrendamiento. Los cálculos definitivos se activarán en la próxima fase.
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          <Clock className="w-3.5 h-3.5" />
          <span>Última actualización del cálculo: <span className="font-medium">{fmtDateTime(rental.last_calculated_at)}</span></span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 1. Datos del inmueble */}
        <Card title="Datos utilizados para el alquiler" icon={Home} testid="rental-card-datos">
          <InfoRow
            label="Municipio"
            tooltip="Municipio del inmueble. Es obligatorio para obtener comparables de alquiler de la base Fotocasa."
            value={asset?.municipio || null}
            help={!asset?.municipio ? '⚠️ El activo no tiene municipio registrado. Se necesita para valorar el alquiler.' : null}
            testid="rental-municipio"
          />
          <InfoRow
            label="Tipo de inmueble"
            tooltip="Tipología del bien. Filtra los comparables por tipos similares (piso, casa, local, etc.)."
            value={asset?.tipo_activo || asset?.tipo_activo_operativo || null}
            testid="rental-tipo"
          />
          <InfoRow
            label="Superficie obtenida del activo"
            tooltip="Metros cuadrados registrados en la ficha del activo. Se usa como valor por defecto para la superficie de alquiler."
            value={automaticArea != null ? `${new Intl.NumberFormat('es-ES').format(automaticArea)} m²` : null}
            testid="rental-superficie-auto"
          />
          <div className="pt-3">
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Superficie utilizada para alquiler
              <HelpHint text="Metros cuadrados usados para calcular el importe de alquiler. Puede diferir de la superficie total si sólo se alquila parte del inmueble." />
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={areaUsed}
                onChange={(e) => setAreaUsed(e.target.value)}
                onBlur={(e) => saveRentalArea(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                }}
                min={0}
                step="1"
                disabled={savingArea}
                className="input-base flex-1 text-sm disabled:opacity-60"
                placeholder="m²"
                data-testid="rental-area-used-input"
              />
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>m²</span>
              <button
                type="button"
                onClick={() => {
                  // Restablecer al valor de la superficie general y borrar el override.
                  const target = automaticArea != null ? String(automaticArea) : '';
                  setAreaUsed(target);
                  saveRentalArea('');
                }}
                disabled={automaticArea == null || savingArea || storedRentalArea == null}
                className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"
                data-testid="rental-area-reset"
                title="Restablecer al valor de la superficie del activo"
              >
                <RotateCcw className="w-3 h-3" />
                Restablecer
              </button>
              {savingArea && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--color-text-muted)' }} />}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {storedRentalArea != null
                ? 'Superficie específica guardada para este activo. Se utilizará siempre en los cálculos de alquiler. Puedes restablecerla a la superficie general.'
                : 'Se está usando la superficie general del activo. Modifícala para guardar una superficie específica sólo para los cálculos de alquiler.'}
            </p>
          </div>
        </Card>

        {/* 2. Precio de alquiler estimado */}
        {/* NOTA: Los campos técnicos (mivau_price_m2, mivau_year, idealista_current_price_m2,
            idealista_three_month_average, evolution_multiplier, updated_price_m2, prudence_percentage)
            se mantienen en el modelo de datos y se gestionan internamente / en Parámetros.
            En esta ficha solo se muestra el precio final estimado. */}
        <Card title="Precio de alquiler estimado" icon={Coins} testid="rental-card-precio-m2">
          <InfoRow
            label="Precio estimado"
            tooltip="Precio de alquiler por m² estimado para la zona. Se calcula a partir de Fotocasa aplicando un criterio prudente."
            value={fmtM2Price(rental.prudent_price_m2)}
            help="Precio estimado para el municipio, actualizado según la evolución del mercado y aplicando un criterio conservador."
            highlight
            testid="rental-prudent-price"
          />
          {/* Características del inmueble — mismo dato compartido con VENTA
              (property_features). Cambios aquí se reflejan también en VENTA. */}
          <div className="pt-4 mt-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <PropertyFeaturesBlock
              asset={asset}
              assetId={asset?.id}
              readOnly={false}
              titleVariant="title"
              showInfo={true}
              onAssetUpdate={onAssetUpdate}
            />
          </div>
        </Card>

        {/* 3. Estimación del alquiler */}
        {/* NOTA: "Alquiler mensual teórico" (rental.theoretical_monthly_rent) se oculta
            visualmente pero se mantiene en el modelo y en el cálculo interno. */}
        <Card title="Estimación de ingresos por alquiler" icon={Wallet} testid="rental-card-ingresos">
          {/* Badge de nivel Fotocasa aplicado + coeficiente de seguridad. */}
          {(asset?.nivel_fuente_alquiler || rental.applied_safety_coefficient != null) && (
            <div className="flex flex-wrap items-center gap-2 pb-3 mb-1 border-b" style={{ borderColor: 'var(--color-border)' }} data-testid="rental-source-badge">
              {asset?.nivel_fuente_alquiler && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border"
                  style={{
                    color: 'var(--color-text-secondary)',
                    background: asset.nivel_fuente_alquiler === 'barrio'
                      ? 'rgba(16,185,129,0.12)'
                      : asset.nivel_fuente_alquiler === 'municipio'
                      ? 'rgba(59,130,246,0.12)'
                      : 'rgba(245,158,11,0.15)',
                    borderColor: 'var(--color-border)',
                  }}
                  title="Nivel de precio Fotocasa aplicado al cálculo de alquiler"
                >
                  Base: {asset.nivel_fuente_alquiler.charAt(0).toUpperCase() + asset.nivel_fuente_alquiler.slice(1)}
                </span>
              )}
              {rental.applied_safety_coefficient != null && rental.applied_rent_source === 'estimated' && (
                <span
                  className="text-[11px]"
                  style={{ color: 'var(--color-text-muted)' }}
                  title="Factor conservador aplicado al precio de mercado antes del ROI"
                >
                  × {rental.applied_safety_coefficient}% coeficiente conservador
                </span>
              )}
              {asset?.sin_precio_mercado && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border border-red-300 bg-red-50 text-red-700">
                  NO HAY DATOS PARA ESTE MICROMERCADO
                </span>
              )}
            </div>
          )}
          <InfoRow
            label="Alquiler mensual estimado"
            tooltip="Renta mensual proyectada = superficie × precio/m² × ratios de prudencia. Sirve como referencia para el ROI de alquiler."
            value={fmtMoney(rental.estimated_monthly_rent) && `${fmtMoney(rental.estimated_monthly_rent)} / mes`}
            highlight
            testid="rental-monthly-estimated"
          />
          <div className="py-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Renta mensual manual (opcional)
              <HelpHint text="Renta introducida manualmente por el usuario. Marca el ROI Alquiler como 'Real' y sobrescribe la estimación automática de Fotocasa." />
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={manualRent}
                onChange={(e) => setManualRent(e.target.value)}
                min={0}
                step="10"
                disabled={savingManualRent}
                className="input-base flex-1 text-sm disabled:opacity-60"
                placeholder="€ / mes"
                data-testid="rental-manual-rent-input"
              />
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>€/mes</span>
            </div>
            {/* Botones de guardado */}
            <div className="flex items-center flex-wrap gap-2 mt-2">
              <button
                type="button"
                onClick={() => saveManualRent('save')}
                disabled={savingManualRent}
                className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-60"
                data-testid="rental-manual-rent-save"
              >
                {savingManualRent ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}
                Guardar y calcular ROI
              </button>
              <button
                type="button"
                onClick={() => saveManualRent('auto')}
                disabled={savingManualRent || rental.manual_monthly_rent == null}
                className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-60"
                data-testid="rental-manual-rent-auto"
                title="Vuelve a usar el alquiler mensual estimado automático"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Usar estimación automática
              </button>
              {manualRentSavedAt && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid="rental-manual-rent-saved-badge"
                  role="status"
                >
                  <Check className="w-3 h-3" />
                  {manualRentSavedAt === 'auto' ? 'Estimación automática restaurada' : 'Guardado y ROI actualizado'}
                </span>
              )}
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
              Utiliza este campo cuando dispongas de una estimación propia, comparables reales o una oferta concreta de alquiler.
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {rental.manual_monthly_rent == null
                ? 'Sin renta manual → se utilizará la renta automática cuando esté disponible.'
                : 'Con renta manual → se utilizará como renta aplicada. La renta automática seguirá siendo visible.'}
            </p>
            {rental.manual_monthly_rent != null && asset?.precio_compra == null && (
              <p className="text-xs mt-1 text-amber-600" data-testid="rental-missing-precio-compra">
                Falta precio de compra para calcular el ROI.
              </p>
            )}
          </div>
          <InfoRow label="Ingresos anuales estimados" tooltip="Renta mensual × 12. Es el numerador del ROI de alquiler." value={fmtMoney(rental.estimated_annual_rent) && `${fmtMoney(rental.estimated_annual_rent)} / año`} testid="rental-annual" />
        </Card>

        {/* 4. Coste estimado de adquisición */}
        <Card title="Inversión estimada" icon={Calculator} testid="rental-card-inversion">
          <InfoRow label="Precio de compra" tooltip="Precio de compra registrado en la ficha del activo. Base para calcular el coste total de adquisición." value={fmtMoney(asset?.precio_compra)} testid="rental-precio-compra" />
          {/* Fila editable: override por-activo del % de gastos de adquisición.
              Valor efectivo = override del activo ?? default global (Parámetros → Alquiler).
              Al vaciar el input y guardar se elimina el override y vuelve al default. */}
          <div className="flex items-start justify-between gap-3 py-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                Gastos de adquisición estimados
                <HelpHint text="% del precio de compra que se destina a gastos (impuestos, notaría, registro, honorarios). Se puede sobrescribir sólo para este activo." />
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                Incluye una estimación conjunta de impuestos, notaría, registro, honorarios y otros gastos de adquisición.
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                {isExpensesPctOverridden
                  ? `Override específico para este activo. Vacía el campo para volver al valor global (${fmtPercent(globalDefaultPct)}).`
                  : `Se está usando el valor global de Parámetros (${fmtPercent(globalDefaultPct)}). Modifícalo para aplicar un porcentaje distinto sólo a este activo.`}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <input
                type="number"
                value={expensesPctInput}
                onChange={(e) => setExpensesPctInput(e.target.value)}
                onBlur={(e) => saveExpensesPct(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                }}
                min={0}
                max={100}
                step="0.5"
                disabled={savingExpensesPct}
                className="input-base w-20 text-sm text-right tabular-nums disabled:opacity-60"
                placeholder={fmtPercent(globalDefaultPct)?.replace(' %', '') || '15'}
                data-testid="rental-expenses-pct-input"
                title={isExpensesPctOverridden
                  ? `Valor específico del activo. Global: ${fmtPercent(globalDefaultPct)}`
                  : `Sin override. Usando valor global: ${fmtPercent(globalDefaultPct)}`}
              />
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>%</span>
              {savingExpensesPct && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--color-text-muted)' }} />}
            </div>
          </div>
          <InfoRow label="Importe estimado de gastos" tooltip="Precio de compra × % de gastos. Se suma al precio de compra para obtener el coste total." value={fmtMoney(rental.acquisition_expenses_amount)} testid="rental-expenses-amount" />
          <InfoRow
            label="Coste total de adquisición"
            tooltip="Precio de compra + gastos estimados. Denominador del ROI de alquiler (ingresos anuales / coste total)."
            value={fmtMoney(rental.total_acquisition_cost)}
            highlight
            testid="rental-total-cost"
          />
        </Card>
      </div>

      {/* 5. Resultado de rentabilidad (tarjeta principal — naranja XÉNICOS) */}
      <div
        className="card-base p-6 border-2"
        style={{ borderColor: '#f97316', background: 'linear-gradient(180deg, rgba(249,115,22,0.06) 0%, transparent 100%)' }}
        data-testid="rental-card-roi"
      >
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5" style={{ color: '#f97316' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>ROI alquiler estimado</h3>
          {rental.applied_rent_source === 'manual' && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200"
              data-testid="rental-roi-manual-badge"
            >
              Renta manual
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>ROI alquiler estimado</p>
            <p className="text-2xl font-bold mt-1" style={{ color: '#f97316' }} data-testid="rental-roi-value">
              {rental.estimated_rental_roi != null ? fmtPercent(rental.estimated_rental_roi, 2) : <Pending text="Pendiente de datos" />}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Alquiler mensual aplicado</p>
            <p className="text-lg font-semibold mt-1" style={{ color: 'var(--color-text-primary)' }} data-testid="rental-applied-monthly">
              {fmtMoney(rental.alquiler_mensual_aplicado) ? `${fmtMoney(rental.alquiler_mensual_aplicado)} / mes` : <Pending text="Pendiente de datos" />}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Alquiler anual aplicado</p>
            <p className="text-lg font-semibold mt-1" style={{ color: 'var(--color-text-primary)' }} data-testid="rental-applied-annual">
              {fmtMoney(rental.estimated_annual_rent) ? `${fmtMoney(rental.estimated_annual_rent)} / año` : <Pending text="Pendiente de datos" />}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Coste total estimado</p>
            <p className="text-lg font-semibold mt-1" style={{ color: 'var(--color-text-primary)' }} data-testid="rental-total-cost-summary">
              {fmtMoney(rental.total_acquisition_cost) || <Pending text="Pendiente de datos" />}
            </p>
          </div>
        </div>
        <p className="text-xs mt-4" style={{ color: 'var(--color-text-muted)' }}>
          Rentabilidad estimada calculada sobre el precio de compra más los gastos de adquisición. No incluye financiación, fiscalidad societaria ni gastos anuales de explotación.
        </p>
      </div>
    </div>
  );
};

export default RentalAnalysisSection;
